import type { DeviceSessionRecord } from './types';
import { getInvestigationDb, loadInvestigationDb, updateInvestigationDb } from './store';

export interface DeviceFingerprint {
  fingerprint: string;
  userAgent: string;
  platform: string;
  language: string;
  screen: string;
  timezone: string;
}

let cachedFingerprint: DeviceFingerprint | undefined;

export async function getDeviceFingerprint(): Promise<DeviceFingerprint> {
  if (cachedFingerprint) return cachedFingerprint;

  const nav = navigator;
  const screenStr = typeof screen !== 'undefined'
    ? `${screen.width}x${screen.height}x${screen.colorDepth}`
    : 'unknown';
  const parts = [
    nav.userAgent,
    nav.platform,
    nav.language,
    screenStr,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    nav.hardwareConcurrency?.toString() || '',
  ];
  const raw = parts.join('|');
  const hash = await sha256Hex(raw);
  cachedFingerprint = {
    fingerprint: hash.slice(0, 32),
    userAgent: nav.userAgent,
    platform: nav.platform,
    language: nav.language,
    screen: screenStr,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  return cachedFingerprint;
}

export async function registerDeviceSession(userId?: string): Promise<DeviceSessionRecord> {
  const fp = await getDeviceFingerprint();
  const now = Date.now();
  let record: DeviceSessionRecord | undefined;

  await updateInvestigationDb((db) => {
    const existing = db.deviceSessions.find((s) => s.fingerprint === fp.fingerprint);
    if (existing) {
      existing.lastSeen = now;
      existing.userId = userId || existing.userId;
      record = existing;
    } else {
      record = {
        fingerprint: fp.fingerprint,
        userId,
        userAgent: fp.userAgent,
        platform: fp.platform,
        firstSeen: now,
        lastSeen: now,
        trusted: false,
      };
      db.deviceSessions.push(record);
    }
  });

  return record!;
}

export async function detectAnomalousSession(userId?: string): Promise<boolean> {
  const fp = await getDeviceFingerprint();
  await loadInvestigationDb();
  const sessions = getInvestigationDb().deviceSessions
    .filter((s) => !userId || s.userId === userId);
  if (sessions.length === 0) return false;
  const known = sessions.some((s) => s.fingerprint === fp.fingerprint);
  return !known;
}

async function sha256Hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
